import { useState } from 'react';
import { setMyGender, type Gender } from '@core/firebase/profile';
import { cn } from '@/lib/cn';
import { pill } from '@/components/PageKit';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Asks for a person's gender at the one point the product needs it.
 *
 * Signup does not ask (App Review 5.1.1(v), 2026-09-19): gender is personal
 * information, and only live sessions use it, because they are same-gender.
 * So the question sits where a session would otherwise be impossible, with
 * the reason stated in the same breath. Twin of the mobile GenderPrompt.
 *
 * The choice is written as it is clicked; the profile hook is live, so the
 * page re-renders with the gender known and this goes away on its own.
 */
export function GenderPrompt({ message, onChoose = setMyGender, testIdPrefix = 'gender' }: {
  message: string;
  /** Who is being described. Defaults to the signed-in person; a parent's
   * children list passes a writer for the child instead. */
  onChoose?: (gender: Gender) => Promise<void>;
  /** Distinct per prompt when a page shows several — one per child. */
  testIdPrefix?: string;
}) {
  const { t } = useLanguage();
  const [chosen, setChosen] = useState<Gender | null>(null);
  const [failed, setFailed] = useState(false);

  const choose = async (gender: Gender) => {
    setChosen(gender);
    setFailed(false);
    try {
      await onChoose(gender);
    } catch {
      // A pressed button after a failed write would claim a gender the server
      // never recorded.
      setChosen(null);
      setFailed(true);
    }
  };

  return (
    <div className="space-y-3 rounded-[18px] bg-tint p-4">
      <p data-testid={`${testIdPrefix}-needed`} className="text-[15px] text-ink">{message}</p>
      <div className="flex gap-2">
        {(['male', 'female'] as const).map((g) => (
          <button
            key={g}
            type="button"
            data-testid={`${testIdPrefix}-${g}`}
            aria-pressed={chosen === g}
            onClick={() => { void choose(g); }}
            className={cn(
              pill.base, pill.md, 'flex-1',
              // Chosen is lime, the redesign's "selected".
              chosen === g ? 'bg-lime text-accent-foreground' : 'bg-card text-ink hover:bg-surface',
            )}
          >
            {t(`auth.nameStep.${g}`)}
          </button>
        ))}
      </div>
      {failed && (
        <p data-testid={`${testIdPrefix}-save-error`} role="alert" className="text-sm text-danger">
          {t('auth.nameStep.genderSaveFailed')}
        </p>
      )}
    </div>
  );
}
